/**
 * Detects and unpacks Claude Design's `__bundler` self-contained HTML format.
 *
 * The bundler envelope stores every asset (JS, fonts) as base64 in a
 * <script type="__bundler/manifest"> tag and replaces UUID placeholders in the
 * HTML template with blob: URLs at load time. This fails when the file is
 * opened via file:// (blob:null/ URLs are blocked by modern browsers) or
 * dropped into the CDC import dialog.
 *
 * This module runs the same decode/decompress logic at import time, converting
 * everything to data: URIs (fonts) or inline source (JS) so the result works
 * in any context — no blob URLs required.
 */

interface ManifestEntry {
  mime: string;
  compressed: boolean;
  data: string; // base64-encoded, optionally gzip-compressed bytes
}

type Manifest = Record<string, ManifestEntry>;

const MANIFEST_RE = /<script\b[^>]*type="__bundler\/manifest"[^>]*>([\s\S]*?)<\/script>/i;
const TEMPLATE_RE = /<script\b[^>]*type="__bundler\/template"[^>]*>([\s\S]*?)<\/script>/i;

/** True when the HTML uses the __bundler envelope format. */
export function isBundlerHtml(html: string): boolean {
  return MANIFEST_RE.test(html);
}

async function decodeEntry(entry: ManifestEntry): Promise<Uint8Array> {
  const binaryStr = atob(entry.data);
  const bytes = new Uint8Array(binaryStr.length);
  for (let i = 0; i < binaryStr.length; i++) {
    bytes[i] = binaryStr.charCodeAt(i)!;
  }
  if (!entry.compressed) return bytes;

  const ds = new DecompressionStream('gzip');
  const writer = ds.writable.getWriter();
  const reader = ds.readable.getReader();
  writer.write(bytes);
  writer.close();

  const chunks: Uint8Array[] = [];
  let total = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    total += value.length;
  }
  const out = new Uint8Array(total);
  let off = 0;
  for (const c of chunks) {
    out.set(c, off);
    off += c.length;
  }
  return out;
}

// Process in 1 KB slices to avoid spreading a huge TypedArray onto the call stack.
function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1024) {
    binary += String.fromCharCode(...bytes.subarray(i, Math.min(i + 1024, bytes.length)));
  }
  return btoa(binary);
}

/**
 * Decode the __bundler format and return a clean standalone HTML document.
 * Throws if the input is not in bundler format or if any asset cannot be decoded.
 */
export async function unbundleHtml(html: string): Promise<string> {
  const manifestMatch = MANIFEST_RE.exec(html);
  if (!manifestMatch?.[1]) throw new Error('[unbundler] __bundler/manifest script tag not found');
  const manifest: Manifest = JSON.parse(manifestMatch[1]);

  const templateMatch = TEMPLATE_RE.exec(html);
  if (!templateMatch?.[1]) throw new Error('[unbundler] __bundler/template script tag not found');
  let template: string = JSON.parse(templateMatch[1]);

  // Decode all assets concurrently.
  const decoded = await Promise.all(
    Object.entries(manifest).map(async ([uuid, entry]) => {
      const bytes = await decodeEntry(entry);
      return { uuid, mime: entry.mime, bytes };
    }),
  );

  // Replace each UUID placeholder in the template.
  for (const { uuid, mime, bytes } of decoded) {
    if (mime === 'application/javascript') {
      // Inline JS as source rather than a data: URI — browsers often block
      // data: URIs used as script src for security reasons.
      const source = new TextDecoder().decode(bytes)
        // A literal `</script>` inside the bundle would terminate the inline block early.
        .replace(/<\/script>/gi, '<\\/script>');
      template = template
        .split(`<script src="${uuid}"></script>`)
        .join(`<script data-cdc-unbundled="1">${source}</script>`);
      // If the UUID still appears in some other form, fall back to a data URI.
      if (template.includes(uuid)) {
        template = template.split(uuid).join(`data:${mime};base64,${bytesToBase64(bytes)}`);
      }
    } else {
      // Fonts and everything else: swap UUID → data URI everywhere it appears.
      template = template.split(uuid).join(`data:${mime};base64,${bytesToBase64(bytes)}`);
    }
  }

  return template;
}
